import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Typography,
} from "@mui/material";
import ApiServices from "../../../Services/ApiServices/ApiServices";

const BusinessesAdd = ({ onSuccess = () => {}, onCancel = () => {} }) => {
  // Business info
  const [name, setName] = useState("");
  const [legalName, setLegalName] = useState("");
  const [ein, setEin] = useState("");
  const [physicalAddress, setPhysicalAddress] = useState("");

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [profit, setProfit] = useState("");
  const [password, setPassword] = useState("");

  // Uploads
  const [idProofImage, setIdProofImage] = useState(null);

  // Owner info
  const [ownerName, setOwnerName] = useState("");
  const [ownerAddress, setOwnerAddress] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerContact, setOwnerContact] = useState("");
  const [ownerSSN, setOwnerSSN] = useState("");
  const [ownerDL, setOwnerDL] = useState("");
  const [ownerIdImage, setOwnerIdImage] = useState(null);

  // Representative info
  const [repName, setRepName] = useState("");
  const [repEmail, setRepEmail] = useState("");
  const [repMobile, setRepMobile] = useState("");

  // Bank details
  const [bankAccount, setBankAccount] = useState("");
  const [bankIFSC, setBankIFSC] = useState("");
  const [bankName, setBankName] = useState("");
  const [bankBranch, setBankBranch] = useState("");
  const [bankAccType, setBankAccType] = useState("");

  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "error" });

  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));

  const validate = () => {
    if (!name.trim()) return setError("Business name is required");
    if (!email.trim()) return setError("Business email is required");

    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRe.test(email)) return setError("Enter a valid email");

    if (!phone.trim()) return setError("Phone is required");

    if (!password || password.length < 6)
      return setError("Password must be at least 6 characters");

    if (!profit) return setError("Profit share percentage is required");
    if (isNaN(profit)) return setError("Enter valid profit %");

    return true;
  };

  const setError = (msg) => {
    setSnack({ open: true, message: msg, severity: "error" });
    return false;
  };

  // ============= SUBMIT =============
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    try {
      const form = new FormData();

      // Business info
      form.append("name", name.trim());
      form.append("legal_name", legalName.trim());
      form.append("ein", ein.trim());
      form.append("physical_address", physicalAddress.trim());

      form.append("email", email.trim());
      form.append("phone", phone.trim());
      form.append("address", address.trim());
      form.append("profit_share_percentage", profit);
      form.append("password", password);

      if (idProofImage) form.append("id_proof_image", idProofImage);

      // Owner info
      form.append("owner_full_name", ownerName);
      form.append("owner_address", ownerAddress);
      form.append("owner_email", ownerEmail);
      form.append("owner_contact_number", ownerContact);
      form.append("owner_ssn", ownerSSN);
      form.append("owner_driving_license_number", ownerDL);

      if (ownerIdImage) form.append("owner_id_image", ownerIdImage);

      // Representative
      form.append("representative_name", repName);
      form.append("representative_email", repEmail);
      form.append("representative_mobile", repMobile);

      // Bank details block as string JSON
      const bankJSON = JSON.stringify({
        account_number: bankAccount,
        ifsc: bankIFSC,
        bank_name: bankName,
        branch: bankBranch,
        account_type: bankAccType,
      });

      form.append("bank_details", bankJSON);

      const res = await ApiServices.post("external-businesses/", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setSnack({ open: true, message: "Business added successfully", severity: "success" });
      onSuccess("Business added successfully", res.data);
    } catch (err) {
      console.log(err);
      let msg = "Failed to add business.";
      if (err?.response?.data) msg = JSON.stringify(err.response.data);
      setSnack({ open: true, message: msg, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ color: "#094c9f", fontWeight: 700 }}>New Business</Typography>

      {/* BASIC INFO */}
      <TextField label="Business Name" value={name} onChange={(e) => setName(e.target.value)} required fullWidth />
      <TextField label="Legal Name" value={legalName} onChange={(e) => setLegalName(e.target.value)} fullWidth />
      <TextField label="EIN" value={ein} onChange={(e) => setEin(e.target.value)} fullWidth />
      <TextField 
        label="Physical Address" 
        value={physicalAddress} 
        onChange={(e) => setPhysicalAddress(e.target.value)} 
        fullWidth 
      />

      <TextField label="Email" value={email} onChange={(e) => setEmail(e.target.value)} required fullWidth />
      <TextField label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} required fullWidth />
      <TextField label="Business Address" value={address} onChange={(e) => setAddress(e.target.value)} fullWidth />

      <TextField
        label="Profit Share %"
        value={profit}
        onChange={(e) => setProfit(e.target.value)}
        type="number"
        inputProps={{ step: "0.01", min: 0 }}
        fullWidth
      />

      <TextField
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        type="password"
        required
        fullWidth
      />

      {/* BUSINESS ID PROOF */}
      <Button variant="outlined" component="label">
        Upload Business ID Proof
        <input type="file" hidden onChange={(e) => setIdProofImage(e.target.files[0])} />
      </Button>

      {/* OWNER DETAILS */}
      <Typography fontWeight={600}>Owner Information</Typography>

      <TextField label="Owner Full Name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} fullWidth />
      <TextField label="Owner Address" value={ownerAddress} onChange={(e) => setOwnerAddress(e.target.value)} fullWidth />
      <TextField label="Owner Email" value={ownerEmail} onChange={(e) => setOwnerEmail(e.target.value)} fullWidth />
      <TextField label="Owner Contact" value={ownerContact} onChange={(e) => setOwnerContact(e.target.value)} fullWidth />
      <TextField label="Owner SSN" value={ownerSSN} onChange={(e) => setOwnerSSN(e.target.value)} fullWidth />
      <TextField label="Owner Driving License Number" value={ownerDL} onChange={(e) => setOwnerDL(e.target.value)} fullWidth />

      <Button variant="outlined" component="label">
        Upload Owner ID Image
        <input type="file" hidden onChange={(e) => setOwnerIdImage(e.target.files[0])} />
      </Button>

      {/* REPRESENTATIVE DETAILS */}
      <Typography fontWeight={600}>Representative Information</Typography>

      <TextField label="Name" value={repName} onChange={(e) => setRepName(e.target.value)} fullWidth />
      <TextField label="Email" value={repEmail} onChange={(e) => setRepEmail(e.target.value)} fullWidth />
      <TextField label="Mobile" value={repMobile} onChange={(e) => setRepMobile(e.target.value)} fullWidth />

      {/* BANK DETAILS */}
      <Typography fontWeight={600}>Bank Details</Typography>

      <TextField label="Account Number" value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} fullWidth />
      <TextField label="IFSC" value={bankIFSC} onChange={(e) => setBankIFSC(e.target.value)} fullWidth />
      <TextField label="Bank Name" value={bankName} onChange={(e) => setBankName(e.target.value)} fullWidth />
      <TextField label="Branch" value={bankBranch} onChange={(e) => setBankBranch(e.target.value)} fullWidth />
      <TextField label="Account Type" value={bankAccType} onChange={(e) => setBankAccType(e.target.value)} fullWidth />

      {/* BUTTONS */}
      <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
        <Button onClick={onCancel} disabled={loading}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={loading} sx={{ backgroundColor: "#094c9f" }}>
          {loading ? <CircularProgress size={20} /> : "Add Business"}
        </Button>
      </Box>

      {/* SNACKBAR */}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
        <Alert severity={snack.severity} onClose={closeSnack}>{snack.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default BusinessesAdd;
